import { Settings } from '../core/settings';
import { Component, ChangeDetectionStrategy, ViewChild, Input, Output, EventEmitter, ElementRef, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { LanguagesStore } from '../core/languages-store';
import { CultureInfo } from '../core/culture-info';
import { FileFormGroup } from './file-form-group';
import { FileFormArray } from './file-form-array';
import { LanguageControl } from './file-language-control';
import { FileNameControl } from './file-name-control';
import { isEmpty, includes } from 'lodash';
import { map } from 'rxjs/operators';
import { UploadedFileResponse } from './uploaded-file-response';

export type FileToLanguage = Map<File, CultureInfo>;

@Component({
    selector: 'covance-file-upload',
    template: require('./covance-file-upload.component.html'),
    styles: [require('./covance-file-upload.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovanceFileUploadComponent implements OnInit {
    @Input()
    public set fileFormArray(value: FileFormArray) {
        this.formArray = value;
        this.filesLanguageMap = new Map<File, CultureInfo>();
    }

    @Input()
    public hideLanguageSelector = false;

    @Input()
    public singleFileOnly = false;

    public get allowedFileExtensions(): string[] {
        return this.fileExtensions;
    }

    @Input()
    public set allowedFileExtensions(value: string[]) {
        this.fileExtensions = value;

        for (let fileGroup of this.formArray.fileFormGroups) {
            fileGroup.fileNameControl.updateAllowedFileExtensions(this.allowedFileExtensions);
        }
    }

    @Input()
    public fileResponses: UploadedFileResponse[] = null;

    @Input()
    public fileMaxSize: number;

    @Input()
    public filesMaxCount: number;

    @Output()
    public filesChanged = new EventEmitter<FileToLanguage>();

    @Input()
    public excludeLanguageIds: string[] = [];

    public excludeLanguageIds$ = new BehaviorSubject<string[]>([]);

    @Input()
    public excludeFileNames: string[] = null;

    @Input()
    public disabled = false;

    public languages: Observable<CultureInfo[]>;
        
    @ViewChild('file')
    public fileControl: ElementRef;

    public filesLanguageMap: FileToLanguage = new Map<File, CultureInfo>();

    public get areEmptyAllowedFileExtensions(): boolean {
        return isEmpty(this.allowedFileExtensions);
    }

    public get allowedFileExtensionsText(): string {
        return !this.areEmptyAllowedFileExtensions
            ? this.allowedFileExtensions.join(' ')
            : '';
    }

    private formArray = new FileFormArray();
    private fileExtensions: string[] = [];

    constructor(
        readonly languagesStore: LanguagesStore,
        private readonly settings: Settings
    ) {
        this.languages = combineLatest([languagesStore.allLanguages, this.excludeLanguageIds$])
            .pipe(map(([languages, excludeLanguageIds]) => languages.filter(l => !includes(excludeLanguageIds, l.languageRowId))));
    }

    public ngOnInit() {
        const inputName: keyof CovanceFileUploadComponent = 'fileFormArray';
        if (!this.formArray) {
            throw new Error(`${inputName} is required input field. Pass an empty formArray instance.`);
        }
        this.excludeLanguageIds$.next(this.excludeLanguageIds);

        if (this.filesMaxCount > 0) {
            this.formArray.setCustomValidators([FileFormArray.maxArrayLengthValidator(this.filesMaxCount)]);
        }
    }

    public browse() {
        this.fileControl.nativeElement.click();
    }

    public onFileControlChanges() {
        const files: FileList = this.fileControl.nativeElement.files;
        this.addFilesToSet(files);
        this.fileControl.nativeElement.value = null;
    }

    public setFileLanguage(fileIndex: number, file: File, languageInfo: CultureInfo) {
        const languageControl = this.formArray.getByIndex(fileIndex).languageControl;

        this.removeLanguageFromExcluded(languageControl.value);
        languageControl.setValue(languageInfo);
        this.filesLanguageMap.set(file, languageInfo);
        this.filesChanged.emit(this.filesLanguageMap);

        this.addLanguageToExcluded(languageInfo);
    }

    public preventDefault(e: DragEvent) {
        e.stopPropagation();
        e.preventDefault();
    }

    public drop(e: DragEvent) {
        this.preventDefault(e);

        if (this.disabled) {
            return;
        }

        if (e.dataTransfer.files.length !== 1 && this.singleFileOnly) {
            return;
        }
        const files = e.dataTransfer.files;
        this.addFilesToSet(files);
    }

    public dragover(e: DragEvent) {
        this.preventDefault(e);
        if (e.dataTransfer.items.length !== 1 && this.singleFileOnly) {
            e.dataTransfer.dropEffect = 'none';
        }
    }
    
    public onDeleteFile(fileIndex: number, file: File) {
        this.removeLanguageFromExcluded(this.filesLanguageMap.get(file));

        this.formArray.removeAt(fileIndex);
        this.filesLanguageMap.delete(file);
        this.filesChanged.emit(this.filesLanguageMap);
    }

    public get files() {
        return Array.from(this.filesLanguageMap.keys());
    }

    public isLanguageInvalid(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).invalid;
    }

    public isLanguageRequired(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).languageControl.hasRequiredError;
    }

    public isLanguageDuplicate(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).languageControl.hasLanguageDuplicateError;
    }

    public isInvalidFileExtension(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).fileNameControl.hasInvalidFileExtension;
    }

    public getInvalidFileExtension(fileIndex: number): string {
        return this.formArray.getByIndex(fileIndex).fileNameControl.invalidFileExtensionError;
    }

    public isInvalidFileSize(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).fileNameControl.hasInvalidFileSize;
    }

    public isInvalidFilesCount(): boolean {
        return this.formArray.hasArrayLengthError;
    }

    public getInvalidFileSize(fileIndex: number): string {
        return this.formArray.getByIndex(fileIndex).fileNameControl.invalidFileSizeError;
    }

    public isDuplicateFile(fileIndex: number): boolean {
        return this.formArray.getByIndex(fileIndex).fileNameControl.hasDuplidateFileError;
    }

    public getUploadedFileResponse(file: File): UploadedFileResponse | null {
        if (isEmpty(this.fileResponses)) {
            return null;
        }

        const fileResponse = this.fileResponses.find(x => x.fileName === file.name);
        return fileResponse;
    }

    private addFilesToSet(files: FileList) {
        if (this.singleFileOnly) {
            this.filesLanguageMap.clear();
            this.formArray.removeAt(0);
        }
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            this.formArray.push(
                new FileFormGroup(
                    new FileNameControl(file.name, 
                                        file.size, 
                                        this.allowedFileExtensions, 
                                        this.fileMaxSize ? this.fileMaxSize : this.settings.maxSizeOfUploadedDocument , 
                                        this.formArray, 
                                        this.excludeFileNames),
                    new LanguageControl(this.formArray, !this.hideLanguageSelector),
                )
            );

            this.filesLanguageMap.set(file, null);
        }

        this.filesChanged.emit(this.filesLanguageMap);
    }

    private removeLanguageFromExcluded(language: CultureInfo) {
        if (language) {
            this.excludeLanguageIds$.next(this.excludeLanguageIds$.value.filter(l => l !== language.languageRowId));
        }
    }

    private addLanguageToExcluded(language: CultureInfo) {
        if (language) {
            this.excludeLanguageIds$.next([...this.excludeLanguageIds$.value, language.languageRowId]);
        }
    }
}